import type { MailboxAccess } from "../services/graphMailService";

/**
 * Mail-Leserecht nach der Rang-Semantik des Backends
 * (`user-mailbox-access.service.ts`: NONE < READ < SEND < FULL).
 *
 * WICHTIG: `SEND` BEINHALTET Lesen — `assertMailRead` lässt SEND-Postfächer
 * durch. Das FE darf deshalb NICHT auf exakte Strings (READ/FULL) filtern:
 * genau das blendete SEND-Postfächer (z. B. cbb@/schaden@) komplett aus dem
 * Mail-Menü, den Ordnerbäumen und den Badges aus (Bug B1).
 */
export function canReadMailbox(m: Pick<MailboxAccess, "mailAccess">): boolean {
    return m.mailAccess === "READ" || m.mailAccess === "SEND" || m.mailAccess === "FULL";
}

/**
 * Mail-Senderecht nach derselben Rang-Semantik (NONE < READ < SEND < FULL):
 * `SEND` und `FULL` dürfen senden, `READ` nicht.
 *
 * Bestimmt, welche Postfächer im „Von"-Dropdown des Verfassen-Fensters stehen
 * und welchem Postfach eine Standard-Signatur zugeordnet werden darf.
 */
export function canSendMailbox(m: Pick<MailboxAccess, "mailAccess">): boolean {
    return m.mailAccess === "SEND" || m.mailAccess === "FULL";
}

/**
 * Default-Postfach für die Termin-ANLAGE (Termine-SP2, Review 2026-07-07):
 * bevorzugt das EIGENE Postfach (Backend-Flag `isOwn`, optional Fallback über
 * die übergebene Login-Adresse), sofern es Kalender-Schreibrecht hat; sonst das
 * erste WRITE-Kalender-Postfach; gibt es GAR keines ⇒ null (Aufrufer zeigt
 * einen sichtbaren Hinweis statt eines stillen No-Ops).
 *
 * Wird von CalendarView/MobileCalendarShell (Grid-Selektion-Fallback) und dem
 * Termin-Formular (Sende-Default, Termine-SP3) gemeinsam genutzt.
 */
export function resolveDefaultCalendarMailbox(
    mailboxes: MailboxAccess[],
    ownAddress?: string | null,
): MailboxAccess | null {
    const writable = mailboxes.filter((m) => m.calendarAccess === "WRITE");
    if (writable.length === 0) return null;
    const own = writable.find(
        (m) =>
            m.isOwn ||
            (ownAddress != null &&
                m.mailboxAddress.toLowerCase() === ownAddress.toLowerCase()),
    );
    return own ?? writable[0];
}
